<script lang="ts">
    import * as Sentry from '@sentry/browser';
    import { onMount } from 'svelte';

    import MultiMediaSelector from '$backoffice/views/MediaManager/MultiMediaSelector.svelte';

    import type { ImageItem } from '../views/MediaManager/types';

    export let inputName: string;
    export let value: string = '[]';
    export let required: boolean = false;
    export let apiEndpoint: string;
    export let mediaBaseUrl: string;
    export let minImages: number = 0;
    export let maxImages: number | null = null;
    export let showCoverLabel: boolean = false;
    export let cropProxyUrl: string;

    let initialImageItems: ImageItem[] = [];
    let isLoading = true;

    onMount(() => {
        try {
            const jsonToParse = value && value.trim() !== '' ? value : '[]';
            const parsedItems = JSON.parse(jsonToParse);

            if (!Array.isArray(parsedItems)) {
                throw new Error('Input value must be an array');
            }

            // Sort by position before mapping
            const sortedItems = [...parsedItems].sort((a, b) => {
                if (
                    typeof a.position === 'number' &&
                    typeof b.position === 'number'
                ) {
                    return a.position - b.position;
                }
                return 0;
            });

            initialImageItems = sortedItems
                .filter(
                    (item) =>
                        item &&
                        typeof item === 'object' &&
                        typeof item.url === 'string',
                )
                .map((item) => {
                    let fullUrl: string;
                    if (
                        /^https?:\/\//.test(item.url) ||
                        item.url.includes('/proxy-image?url=')
                    ) {
                        fullUrl = item.url;
                    } else {
                        const baseUrl = mediaBaseUrl.endsWith('/')
                            ? mediaBaseUrl.slice(0, -1)
                            : mediaBaseUrl;
                        const relativePath = item.url.startsWith('/')
                            ? item.url.substring(1)
                            : item.url;
                        fullUrl = `${baseUrl}/assets/${relativePath}`;
                    }
                    const focalPointStr: unknown = item.focalPoint;
                    let focalPoint: { x: number; y: number } | undefined;
                    if (typeof focalPointStr === 'string') {
                        const parts = focalPointStr.split(' ');
                        const x = parseFloat(parts[0]);
                        const y = parseFloat(parts[1]);
                        if (!isNaN(x) && !isNaN(y)) {
                            focalPoint = { x, y };
                        }
                    }

                    const imageWidth =
                        typeof item.imageWidth === 'number'
                            ? item.imageWidth
                            : undefined;
                    const imageHeight =
                        typeof item.imageHeight === 'number'
                            ? item.imageHeight
                            : undefined;

                    return {
                        id: item.id,
                        tempId: `initial-${item.id || item.url}-${Math.random()
                            .toString(36)
                            .substring(2, 9)}`,
                        status: 'uploaded' as const,
                        path: fullUrl,
                        width: imageWidth,
                        height: imageHeight,
                        position: item.position,
                        focalPoint,
                    };
                });
        } catch (e) {
            console.error('Failed to parse initial value JSON:', e);
            Sentry.captureException(e, {
                extra: {
                    inputValue: value,
                    context: 'MultiMediaManagerController.onMount',
                    error: e instanceof Error ? e.message : String(e),
                },
            });
            // Set empty array as fallback
            initialImageItems = [];
        } finally {
            isLoading = false;
        }
    });
</script>

<div class="form-control">
    {#if isLoading}
        <p>...</p>
    {:else}
        <MultiMediaSelector
            {inputName}
            {initialImageItems}
            {required}
            {apiEndpoint}
            {mediaBaseUrl}
            {minImages}
            {maxImages}
            {showCoverLabel}
            {cropProxyUrl}
        />
    {/if}
</div>
